// Native on-duty location bridge.
//
// This is a thin capability adapter, not a second tracking system. When the app
// runs inside the MissionReady native shell (Capacitor) the native layer owns
// location while the guard is on duty, because only it can keep reporting while
// the app is backgrounded or the screen is locked. Everywhere else (mobile web,
// installed PWA, desktop) the existing web tracker stays in charge, unchanged.
//
// Handoff rule — exactly one reporter at a time:
//   native shell + plugin present + on duty  -> native reports, web tracker off
//   anything else                            -> web tracker reports as it does today
//
// Tracking lifecycle is driven solely by the open `time_entries` punch. The site
// geofence is never consulted here.

export type NativeDutyStatus = {
  available: boolean;
  platform?: "ios" | "android" | string;
  tracking: boolean;
  foregroundPermission: boolean;
  backgroundPermission: boolean;
  authorization?: string;
  lastFixAt?: string | null;
  queued?: number;
};

type DutyLocationPlugin = {
  status(): Promise<NativeDutyStatus>;
  requestPermissions(opts?: { background?: boolean }): Promise<NativeDutyStatus>;
  start(opts: {
    // Android uploads through the signed /api/public/location-ping endpoint; the
    // shared secret lives in the native build config, never in the web bundle.
    officerId: string;
    shiftId?: string | null;
    organizationId?: string;
    siteId?: string | null;
    timeEntryId?: string | null;
    // Legacy iOS direct-upload fields (unused by the Android service).
    supabaseUrl?: string;
    apiKey?: string;
    accessToken?: string;
    minIntervalMs?: number;
    minDistanceM?: number;
  }): Promise<NativeDutyStatus>;
  updateAuth(opts: { accessToken?: string; siteId?: string | null; shiftId?: string | null }): Promise<void>;
  stop(): Promise<void>;
};

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: Record<string, unknown>;
};

function capacitor(): CapacitorGlobal | null {
  if (typeof window === "undefined") return null;
  const cap = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
  return cap ?? null;
}

/** True only inside the native iOS/Android shell. */
export function isNativeShell(): boolean {
  const cap = capacitor();
  return !!cap?.isNativePlatform?.();
}

export function nativePlatform(): string | null {
  return capacitor()?.getPlatform?.() ?? null;
}

function plugin(): DutyLocationPlugin | null {
  if (!isNativeShell()) return null;
  const p = capacitor()?.Plugins?.["DutyLocation"];
  return (p as DutyLocationPlugin | undefined) ?? null;
}

/** Present only when the native shell exposes the on-duty location plugin. */
export function hasNativeDutyLocation(): boolean {
  return plugin() !== null;
}

export async function nativeDutyStatus(): Promise<NativeDutyStatus | null> {
  const p = plugin();
  if (!p) return null;
  try { return await p.status(); } catch { return null; }
}

/**
 * Staged permission request. Only called from an on-duty moment (clock-in with
 * consent already given) — never at install or app launch.
 */
export async function requestNativeDutyPermissions(background: boolean): Promise<NativeDutyStatus | null> {
  const p = plugin();
  if (!p) return null;
  try { return await p.requestPermissions({ background }); } catch { return null; }
}

export async function startNativeDutyTracking(opts: {
  officerId: string;
  shiftId?: string | null;
  organizationId?: string;
  siteId?: string | null;
  timeEntryId?: string | null;
  supabaseUrl?: string;
  apiKey?: string;
  accessToken?: string;
}): Promise<NativeDutyStatus | null> {
  const p = plugin();
  if (!p) return null;
  try {
    return await p.start({ minIntervalMs: 30_000, minDistanceM: 25, ...opts });
  } catch {
    return null;
  }
}

export async function updateNativeDutyAuth(
  accessToken: string,
  siteId?: string | null,
  shiftId?: string | null,
) {
  const p = plugin();
  if (!p) return;
  try { await p.updateAuth({ accessToken, siteId, shiftId }); } catch { /* retried on next refresh */ }
}

export async function stopNativeDutyTracking() {
  const p = plugin();
  if (!p) return;
  try { await p.stop(); } catch { /* service also stops itself when the task ends */ }
}
